import { ValidateResult, Validator } from './Validator';
import {
   formatDate,
   formatMessageBundle, formatTime,
   InvalidValue,
   parseDecimal,
   parseInteger
} from '@unifocus/js-utils';
import messages from '../../../localized-messages';
import { LocalDate, LocalTime } from '@js-joda/core';

export const requiredValidator = (required?: boolean) => (value?: any): ValidateResult => {
   if (required) {
      return value ? undefined : formatMessageBundle(messages, 'res_requiredValidationError');
   }

   return undefined;
};

export const minValueValidator = (min?: number) => (value?: any): ValidateResult => {
   if (min && value && !isNaN(value)) {
      return value >= min ? undefined : formatMessageBundle(messages, 'res_minValidationError', [min.toString()]);
   }

   return undefined;
};

export const maxValueValidator = (max?: number) => (value?: any): ValidateResult => {
   if (max && value && !isNaN(value)) {
      return value <= max ? undefined : formatMessageBundle(messages, 'res_maxValidationError', [max.toString()]);
   }

   return undefined;
};

export const minMaxValueValidator = (min?: number, max?: number) => (value?: any): ValidateResult => {

   if (value !== undefined && typeof value === "number" && !isNaN(value)) {

      if (min !== undefined && max !== undefined) {
         if (value < min || value > max) {
            return formatMessageBundle(messages,
                                       'res_minMaxValidationError',
                                       [min.toString(), max.toString()]);
         }
      }
      else if (min !== undefined) {
         if (value < min) {
            return formatMessageBundle(messages, 'res_minValidationError', [min.toString()]);
         }
      }
      else if (max !== undefined) {
         if (value > max) {
            return formatMessageBundle(messages, 'res_maxValidationError', [max.toString()]);
         }
      }
   }

   return undefined;
};

export const minLengthValidator = (minLength?: number) => (value?: any): ValidateResult => {
   if (minLength) {
      if (typeof value === 'string') {
         return (value as string).length >= minLength ? undefined : formatMessageBundle(messages, 'res_minLengthValidationError', [minLength.toString()]);
      }
   }

   return undefined;
};

export const maxLengthValidator = (maxLength?: number) => (value?: any): ValidateResult => {
   if (maxLength) {
      if (typeof value === 'string') {
         return (value as string).length <= maxLength ? undefined : formatMessageBundle(messages, 'res_maxLengthValidationError', [maxLength.toString()]);
      }
   }

   return undefined;
};

export const minMaxLengthValidator = (minLength?: number, maxLength?: number) => (value?: any): ValidateResult => {

   if (value !== undefined && typeof value === 'string') {

      if (minLength !== undefined && maxLength !== undefined) {
         if ((value as string).length < minLength || (value as string).length > maxLength) {
            return formatMessageBundle(messages,
                                       'res_minMaxLengthValidationError',
                                       [minLength.toString(), maxLength.toString()]);
         }
      }
      else if (minLength !== undefined) {
         if ((value as string).length < minLength) {
            return formatMessageBundle(messages, 'res_minLengthValidationError', [minLength.toString()]);
         }
      }
      else if (maxLength !== undefined) {
         if ((value as string).length > maxLength) {
            return formatMessageBundle(messages, 'res_maxLengthValidationError', [maxLength.toString()]);
         }
      }
   }

   return undefined;
};

export const patternValidator = (pattern?: RegExp, failureMessage?: string) => (value?: any): ValidateResult => {
   if (pattern) {
      if (typeof value === 'string') {
         return (pattern.test(value)) ? undefined : failureMessage;
      }
   }

   return undefined;
};

export const validateValidator = (validate?: Validator) => (value?: any): ValidateResult => {
   if (validate) {
      return validate(value);
   }

   return undefined;
};

export const decimalValidator = (value?: any): ValidateResult => {
   if (value === InvalidValue) {
      return formatMessageBundle(messages, 'res_invalidNumberValidationError');
   }

   if (value && typeof value === 'string') {
      return isNaN(parseDecimal(value)) ? formatMessageBundle(messages, 'res_invalidNumberValidationError') : undefined;
   }

   return undefined;
};

export const integerValidator = (value?: any): ValidateResult => {
      if (value === InvalidValue) {
         return formatMessageBundle(messages, 'res_invalidNumberValidationError');
      }

   if (value && typeof value === 'string') {
      return isNaN(parseInteger(value)) ? formatMessageBundle(messages, 'res_invalidNumberValidationError') : undefined;
   }

   return undefined;
};

export const localDateValidator =  (value?: any): ValidateResult => {
   if (value === InvalidValue) {
      return formatMessageBundle(messages, 'res_invalidDateValidationError');
   }
   return undefined;
};

export const localDateRangeValidator =  (value?: any): ValidateResult => {
   if (value === InvalidValue) {
      return formatMessageBundle(messages, 'res_invalidDateRangeValidationError');
   }
   return undefined;
};

export const localTimeValidator =  (value?: any): ValidateResult => {
   if (value === InvalidValue) {
      return formatMessageBundle(messages, 'res_invalidTimeValidationError');
   }
   return undefined;
};

export const multiValidator = (validators: Validator[]) => (value?: any) => {
   return validators.reduce((error: ValidateResult, validator) => error || validator(value), undefined);
};

export const minLocalDateValidator = (min?: LocalDate) => (value?: any): ValidateResult => {
   if (min && value && value instanceof LocalDate) {
      return !value.isBefore(min) ? undefined : formatMessageBundle(messages, 'res_minDateValidationError', [formatDate(min)]);
   }

   return undefined;
};

export const maxLocalDateValidator = (max?: LocalDate) => (value?: any): ValidateResult => {
   if (max && value && value instanceof LocalDate) {
      return !value.isAfter(max) ? undefined : formatMessageBundle(messages, 'res_maxDateValidationError', [formatDate(max)]);
   }

   return undefined;
};

export const minMaxLocalDateValidator = (minDate?: LocalDate, maxDate?: LocalDate) => (value?: any): ValidateResult => {

   if (value !== undefined && value instanceof LocalDate) {

      if (minDate !== undefined && maxDate !== undefined) {
         if (value.isBefore(minDate) || value.isAfter(maxDate)) {
            return formatMessageBundle(messages,
                                       'res_minMaxDateValidationError',
                                       [formatDate(minDate), formatDate(maxDate)]);
         }
      }
      else if (minDate !== undefined) {
         if (value.isBefore(minDate)) {
            return formatMessageBundle(messages, 'res_minDateValidationError', [formatDate(minDate)]);
         }
      }
      else if (maxDate !== undefined) {
         if (value.isAfter(maxDate)) {
            return formatMessageBundle(messages, 'res_maxDateValidationError', [formatDate(maxDate)]);
         }
      }
   }

   return undefined;
};

export const minMaxLocalTimeValidator = (minTime?: LocalTime, maxTime?: LocalTime) => (value?: any): ValidateResult => {

   if (value !== undefined && value instanceof LocalTime) {

      if (minTime !== undefined && maxTime !== undefined) {
         if (value.isBefore(minTime) || value.isAfter(maxTime)) {
            return formatMessageBundle(messages,
                                       'res_minMaxTimeValidationError',
                                       [formatTime(minTime), formatTime(maxTime)]);
         }
      }
      else if (minTime !== undefined) {
         if (value.isBefore(minTime)) {
            return formatMessageBundle(messages, 'res_minTimeValidationError', [formatTime(minTime)]);
         }
      }
      else if (maxTime !== undefined) {
         if (value.isAfter(maxTime)) {
            return formatMessageBundle(messages, 'res_maxTimeValidationError', [formatTime(maxTime)]);
         }
      }
   }

   return undefined;
};
